import type { FC } from 'react';
import { useHistory } from 'react-router';
import { useApolloClient } from '@apollo/client';
import { printFormat } from 'iban';

import { TeamMembersRequestStatus } from '__generated__/GQL';
import { formatAmount } from 'common/formatAmount';
import useCompanyContext from 'common/hooks/useCompanyContext';
import SunshineCard from 'components/_core/SunshineCard';
import CancelTeamMemberRequestSidebar, {
  CancelTeamMemberRequestSidebarProps,
} from 'components/CancelTeamMemberRequestSidebar';
import ErrorCard from 'components/ErrorCard';
import Loader from 'components/Loader';
import SupportingDocumentPreview from 'components/SupportingDocumentPreview';
import TransferInformationRow from 'components/TransferInformationRow';
import { formatDate, PPP } from 'helpers/date';
import { getBankTransferStatusDate } from 'helpers/teamMembersRequests/bankTransfer';

import { useExpenseClaimRequestDetail } from '../../../../hooks/useExpenseClaimRequestDetail';
import * as routes from '../../../../routes';
import { RequestStatusCallout } from './components/RequestStatusCallout';
import { locales } from './locales';

interface ExpenseClaimRequestSidebarProps {
  requestId: string;
}

export const ExpenseClaimRequestSidebar: FC<
  ExpenseClaimRequestSidebarProps
> = ({ requestId }) => {
  const { companyProfileId } = useCompanyContext();
  const { error, expenseClaimRequest, loading } =
    useExpenseClaimRequestDetail(requestId);
  const history = useHistory();
  const client = useApolloClient();

  const handleOnCompleted: CancelTeamMemberRequestSidebarProps['onCompleted'] =
    (data) => {
      if (data.cancelTeamMembersRequest.__typename === 'ExpenseClaimRequest') {
        client.cache.evict({
          fieldName: 'teamMembersRequests',
          id: client.cache.identify({
            __typename: 'Company',
            companyProfileId,
          }),
        });
        client.cache.gc();
      }
      history.replace(routes.root);
    };

  if (error) {
    return <ErrorCard />;
  }

  if (loading || !expenseClaimRequest) {
    return <Loader />;
  }

  const amount = formatAmount({
    forcePlusSign: false,
    value: expenseClaimRequest.requesterPayload.amount.total / 100,
    withNoMinorUnitDigits: true,
  });

  const recipient = `${
    expenseClaimRequest.requesterPayload.bankTransferRecipient.name
  }
${printFormat(expenseClaimRequest.requesterPayload.bankTransferRecipient.iban)}
  `;

  return (
    <CancelTeamMemberRequestSidebar
      canCancelRequest={
        expenseClaimRequest.status === TeamMembersRequestStatus.Pending
      }
      closeTo={routes.root}
      colorScheme="grey"
      onCompleted={handleOnCompleted}
      requestId={expenseClaimRequest.teamMembersRequestId}
    >
      <CancelTeamMemberRequestSidebar.Header
        heading={amount}
        iconName="transaction-out"
        label={locales.transactionOut}
      />
      <CancelTeamMemberRequestSidebar.Body alignItems="center">
        <RequestStatusCallout request={expenseClaimRequest} />
        {expenseClaimRequest.supportingDocumentsUrls.length > 0 &&
        expenseClaimRequest.supportingDocumentsUrls[0] ? (
          <SupportingDocumentPreview
            href={expenseClaimRequest.supportingDocumentsUrls[0]}
            marginTop="space-24"
          />
        ) : null}

        <SunshineCard.Group isBoxed={false} marginTop="space-16">
          <TransferInformationRow label={locales.amountLabel} value={amount} />
          {expenseClaimRequest.requesterPayload.amount.vatItems.map(
            ({ amount: vatAmount, rate }, i) => (
              <TransferInformationRow
                key={i}
                label={locales.formatString(locales.vatRate, {
                  rate: rate ? Number((rate * 100).toFixed(2)) : 0,
                })}
                value={formatAmount({
                  forcePlusSign: false,
                  value: vatAmount / 100,
                  withNoMinorUnitDigits: true,
                })}
              />
            ),
          )}
          <TransferInformationRow
            label={locales.executionDateLabel[expenseClaimRequest.status]}
            value={formatDate(
              getBankTransferStatusDate(expenseClaimRequest),
              PPP,
            )}
          />
          <TransferInformationRow
            label={locales.recipientLabel}
            value={recipient}
          />
          <TransferInformationRow
            label={locales.description}
            value={expenseClaimRequest.requesterPayload.description}
          />
          <TransferInformationRow
            label={locales.reseller}
            value={expenseClaimRequest.requesterPayload.reseller}
          />
        </SunshineCard.Group>
      </CancelTeamMemberRequestSidebar.Body>
    </CancelTeamMemberRequestSidebar>
  );
};
